/**
 * The first JSON object in a model's reply, closed if the model forgot to close it.
 *
 * The chat model (gpt-5-mini) sometimes ends a perfectly good object one bracket short: a golf
 * layout proposal came back as `…}]}]` with the root's `}` never written — 645 tokens, nowhere
 * near its cap, so this is the model dropping a character, not a reply cut off. One in two of
 * the replies sampled on 2026-09-27 did it, and every one failed the layout step with "Could not
 * work out layouts just now".
 *
 * So: read from the first `{`, tracking strings, and stop where the object closes — anything
 * after it (a sentence, a code fence) is not part of it. If the text ends with brackets still
 * open, close them in order. A closer that does not match what is open is NOT guessed at: that
 * reply is malformed in a way a repair could make wrong, and it is refused instead.
 */
export function balanceJson(text: string): string | null {
  const start = text.indexOf('{');
  if (start < 0) return null;
  const open: string[] = [];
  let inString = false;
  let escaped = false;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === '{') open.push('}');
    else if (ch === '[') open.push(']');
    else if (ch === '}' || ch === ']') {
      if (open.pop() !== ch) return null;
      if (open.length === 0) return text.slice(start, i + 1);
    }
  }
  // Ran out of text inside a string: the reply was cut mid-value, which nothing can close.
  if (inString) return null;
  const body = text.slice(start).replace(/[\s,]+$/, '');
  return body + open.reverse().join('');
}

/** `JSON.parse` of the first object in a reply, closing what the model left open. */
export function parseModelJson(text: string): unknown {
  const balanced = balanceJson(text);
  if (balanced === null) return null;
  try {
    return JSON.parse(balanced);
  } catch {
    return null;
  }
}
